import { useTelemetryStore } from "../stores/telemetryStore";

/**
 * Maps asset series to a GLB path under web/public/models/.
 * Add or replace entries here as new models are dropped into the directory.
 * Phase 2 will replace this with a runtime lookup from the Asset Registry API.
 */
const SERIES_MODEL: Record<string, string> = {
  F: "/models/forklift_f.glb",  // counterbalance / reach truck
  P: "/models/forklift_p.glb",  // pallet jack
  M: "/models/forklift_m.glb",  // order picker
  S: "/models/forklift_s.glb",  // stacker
};

/**
 * Extra yaw applied to the MODEL, in radians — the knob for "the truck faces
 * the wrong way".
 *
 * Two independent things decide which way a forklift points, and they are kept
 * apart on purpose:
 *
 *  1. The world heading. Telemetry `yaw_rad` is atan2(dy, dx), measured from
 *     +x — verified against the wire: a truck driving -x reports 3.142 and
 *     atan2 of its own deltas is 3.142. The scene maps (x, y) to world (x, z),
 *     and a three.js rotation.y of phi sends local +x to (cos phi, 0, -sin phi),
 *     so the heading is `-yaw`, never `+yaw`. Forklift.tsx owns this and it is
 *     the same for every model.
 *
 *  2. Which way the mesh was authored. That is per model, and it is this table.
 *
 * Measured from the shipped GLBs: all four have their long axis on Z, while the
 * procedural fallback is built nose-along-+X. That 90 degrees is why the trucks
 * tracked sideways.
 *
 * Which end LEADS is a fact about the vehicle, not about the mesh. The forks --
 * and the pallet on them -- sit at the +Z extreme in all four models
 * (forklift_f: pallet Z [-40, -20] against a body reaching -443; forklift_m:
 * pallet Z [88, 108]). But a reach truck, stacker, order picker or pallet truck
 * TRAVELS FORKS-TRAILING: the drive end leads and the load follows. So the
 * leading end is -Z, not the fork end, and the offset is -PI/2.
 *
 * Exceptions are rare but real -- a true counterbalance truck under load drives
 * forks-forward. That is what this table is for: give the series its own entry
 * with PI added, and nothing else moves.
 *
 * Offsets, given the `-yaw` heading above:
 *    nose +x -> 0          nose -x ->  Math.PI
 *    nose +z -> Math.PI/2  nose -z -> -Math.PI/2
 * If a model comes out exactly backwards, add Math.PI to its entry.
 */
const SERIES_MODEL_YAW: Record<string, number> = {
  F: -Math.PI / 2,  // counterbalance / reach
  P: -Math.PI / 2,  // pallet truck
  M: -Math.PI / 2,  // order picker
  S: -Math.PI / 2,  // stacker
};

/** Default for an uploaded model with no entry: the shipped convention. */
export const DEFAULT_MODEL_YAW = -Math.PI / 2;

export function modelYawForSeries(series: string): number {
  return SERIES_MODEL_YAW[series] ?? DEFAULT_MODEL_YAW;
}

// Uniform scale applied to all forklift GLBs. Tune until forklifts look right
// relative to the warehouse model. Phase 2 will replace this with per-asset
// scale from the Asset Registry.
export const FORKLIFT_MODEL_SCALE = 0.5;

/** Returns the GLB URL for a given asset series, falling back to "F" for unknown codes. */
export function modelUrlForSeries(series: string): string {
  return SERIES_MODEL[series] ?? SERIES_MODEL["F"];
}

/**
 * Returns the registry-provided model URL for a specific asset if available,
 * falling back to the static series map. This allows operators to upload
 * custom models per series without a code deploy.
 */
export function modelUrlForAsset(assetId: string, series: string): string {
  const fork = useTelemetryStore.getState().forklifts[assetId];
  if (fork?.modelUrl) return fork.modelUrl;
  return modelUrlForSeries(series);
}

/** Preload URLs so three.js has them cached before forklifts appear. */
export const ALL_MODEL_URLS = Object.values(SERIES_MODEL);
